
import React from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteButton } from "@/components/common/DeleteButton";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import type { Candidate } from "./types";

interface CandidateTableRowProps {
  candidate: Candidate;
}

const CandidateTableRow: React.FC<CandidateTableRowProps> = ({ 
  candidate
}) => {
  const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'screening': return 'bg-yellow-100 text-yellow-800';
      case 'interviewing': return 'bg-purple-100 text-purple-800';
      case 'offered': return 'bg-orange-100 text-orange-800';
      case 'hired': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="font-mono text-sm font-medium">
          {candidate.candidate_number || 'N/A'}
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <Link 
          to={`/candidates/${candidate.id}`}
          className="hover:underline"
        >
          {candidateName}
        </Link>
      </TableCell>
      <TableCell>{candidate.email}</TableCell>
      <TableCell>{candidate.phone}</TableCell>
      <TableCell>
        <Badge className={getStatusColor(candidate.status || 'new')}>
          {candidate.status || 'new'}
        </Badge>
      </TableCell>
      <TableCell>{candidate.source}</TableCell>
      <TableCell>
        {candidate.created_at 
          ? formatDistanceToNow(new Date(candidate.created_at), { addSuffix: true })
          : 'Unknown'
        }
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/candidates/${candidate.id}`}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/candidates/${candidate.id}`} className="flex items-center">
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <DeleteButton
                  entityType="candidate"
                  entityId={candidate.id}
                  entityName={candidateName}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-destructive hover:text-destructive"
                >
                  Delete Candidate
                </DeleteButton>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default CandidateTableRow;
